import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, map, of, switchMap, take } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthentificationService } from '@app/common-codes/services/authentification.service';
import { RuntimeProfileService } from '@app/common-codes/services/RuntimeProfileService';

// Titres de rôle (claim matwins_role / account.roleTitle) confinés au kiosque.
const VISITOR_ROLE_TITLES = ['visitor', 'visiteur'];
// Périmètre kiosque : couvre /ipad, ses enfants et /ipad-pos-choice.
const KIOSK_URL_PREFIX = '/ipad';
const KIOSK_HOME_URL = '/ipad';
const KIOSK_ERROR_URL = '/ipad/error';

/** « Visitor » / « Visiteur » : seuls ces titres de rôle sont confinés au kiosque. */
export function isVisitorRoleTitle(title: string | null | undefined): boolean {
    return !!title && VISITOR_ROLE_TITLES.includes(title.trim().toLowerCase());
}

/**
 * Confinement kiosque : un compte connecté avec le rôle « Visitor » est renvoyé
 * vers /ipad sur toute navigation qui sort du périmètre kiosque, y compris les
 * pages d'erreur (/unauthorized, routes inconnues).
 */
@Injectable({
    providedIn: 'root'
})
export class VisitorContainmentGuard implements CanActivate {
    constructor(
        private oidcSecurityService: OidcSecurityService,
        private authService: AuthentificationService,
        private router: Router,
        private runtimeProfile: RuntimeProfileService
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean | UrlTree> {
        if (state.url.startsWith(KIOSK_URL_PREFIX)) {
            return of(true);
        }

        // Les routes d'erreur affichent la page d'erreur du kiosque plutôt que sa page d'accueil.
        const isErrorRoute = route.routeConfig?.path === 'unauthorized' || route.routeConfig?.path === '**';

        return this.roleTitle$().pipe(
            map(roleTitle => {
                if (!isVisitorRoleTitle(roleTitle)) return true;
                return this.router.parseUrl(isErrorRoute ? KIOSK_ERROR_URL : KIOSK_HOME_URL);
            })
        );
    }

    /** Claim matwins_role du jeton (disponible dès checkAuth), sinon rôle du compte chargé. */
    private roleTitle$(): Observable<string | undefined> {
        const accountRoleTitle = this.authService.currentUser?.roleTitle ?? this.authService.currentUser?.role?.title;
        if (this.runtimeProfile.skipsAuthentication) {
            return of(accountRoleTitle);
        }
        // Pages publiques (ex. /qrcode) : aucun jeton en session, on ne lit pas le
        // payload OIDC sinon la lib loggue "token is not valid" à chaque navigation.
        return this.oidcSecurityService.isAuthenticated$.pipe(
            take(1),
            switchMap(({ isAuthenticated }) => {
                if (!isAuthenticated) return of(accountRoleTitle);
                return this.oidcSecurityService.getPayloadFromIdToken().pipe(
                    map(payload => payload?.['matwins_role'] ?? accountRoleTitle)
                );
            })
        );
    }
}
